import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import {
  Check,
  Minus,
  ArrowRight,
  Target,
  Receipt,
  Map,
  Camera,
  Calculator,
  Layers,
  Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';

export const ComparePage = () => {
  return (
    <>
      <SEOHead
        title="Compare Roofing Software — Rooflyzer vs Competitors"
        description="Compare Rooflyzer OS against JobNimbus, AccuLynx, Roofr, and Leap. See which roofing CRM delivers more value for your contracting business."
        canonicalUrl="/compare"
        keywords="roofing software comparison, rooflyzer vs jobnimbus, best roofing CRM, roofing software reviews"
        ogImage="/social-card.svg"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rooflyzer.pro' },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Compare',
              item: 'https://rooflyzer.pro/compare',
            },
          ],
        }}
      />

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20 relative overflow-hidden">
        {/* Global Tech Grid Overlay */}
        <div className="absolute inset-0 tech-grid-bg opacity-[0.05] pointer-events-none -z-10" />

        {/* Header Section */}
        <div className="max-w-5xl mx-auto text-center mb-20 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-800/80 border border-slate-700 mb-6"
          >
            <Layers className="size-4 text-slate-300" />
            <span className="text-xs font-mono font-medium text-slate-300 uppercase tracking-wider">
              Market Assessment
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-6 leading-tight tracking-tight"
          >
            Stop paying <span className="headline-gradient">$30 per roof report</span>.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed"
          >
            We believe rooftop data should be a commodity, not a luxury. See how we stack up against
            the legacy "big guys" on capabilities, speed, and bottom-line value.
          </motion.p>
        </div>

        {/* Comparison Table Container */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-4xl mx-auto bg-slate-900/60 rounded-3xl overflow-hidden mb-28 relative z-10 shadow-2xl border border-white/10"
        >
          <div className="absolute inset-0 tech-grid-bg opacity-[0.03] pointer-events-none" />
          <div className="overflow-x-auto w-full">
            <table className="w-full text-left border-collapse text-sm sm:text-base">
              <thead>
                <tr className="border-b border-white/10 bg-slate-900/90">
                  <th className="p-4 sm:p-5 text-white/90 font-bold text-left">Feature</th>
                  <th className="p-4 sm:p-5 text-white font-extrabold bg-slate-800/50 border-x border-white/10 text-center">
                    <span className="flex items-center justify-center gap-1.5 text-xs sm:text-sm md:text-lg">
                      <Sparkles className="size-4 text-emerald-400" />
                      Rooflyzer OS
                    </span>
                  </th>
                  <th className="p-4 sm:p-5 text-white/70 font-semibold text-center text-xs sm:text-sm md:text-base">
                    Roofr*
                  </th>
                  <th className="p-4 sm:p-5 text-white/70 font-semibold text-center text-xs sm:text-sm md:text-base">
                    EagleView*
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 text-white/80">
                <tr className="hover:bg-white/[0.03] transition-colors">
                  <td className="p-4 sm:p-5 font-bold text-white/90">Measurement Reports</td>
                  <td className="p-4 sm:p-5 text-center bg-slate-800/30 border-x border-white/10">
                    <span className="text-emerald-400 font-extrabold text-xs sm:text-sm md:text-base">
                      Unlimited Free**
                    </span>
                  </td>
                  <td className="p-4 sm:p-5 text-center text-white/80 text-xs sm:text-sm">
                    $15 - $40 / report
                  </td>
                  <td className="p-4 sm:p-5 text-center text-white/80 text-xs sm:text-sm">
                    $30 - $75 / report
                  </td>
                </tr>
                <tr className="hover:bg-white/[0.03] transition-colors">
                  <td className="p-4 sm:p-5 font-bold text-white/90">Turnaround Time</td>
                  <td className="p-4 sm:p-5 text-center bg-slate-800/30 border-x border-white/10">
                    <span className="text-white font-extrabold text-xs sm:text-sm md:text-base">
                      Instant (Real-time)
                    </span>
                  </td>
                  <td className="p-4 sm:p-5 text-center text-white/80 text-xs sm:text-sm">
                    4 hrs - 24 hrs
                  </td>
                  <td className="p-4 sm:p-5 text-center text-white/80 text-xs sm:text-sm">
                    24 hrs - 48 hrs
                  </td>
                </tr>
                <tr className="hover:bg-white/[0.03] transition-colors">
                  <td className="p-4 sm:p-5 font-bold text-white/90">Interactive BrandKit™</td>
                  <td className="p-4 sm:p-5 text-center bg-slate-800/30 border-x border-white/10 text-emerald-400">
                    <Check className="mx-auto w-5 h-5 sm:w-6 sm:h-6 stroke-[3px]" />
                  </td>
                  <td className="p-4 sm:p-5 text-center text-white/30">
                    <Minus className="mx-auto w-5 h-5 sm:w-6 sm:h-6 stroke-[2px]" />
                  </td>
                  <td className="p-4 sm:p-5 text-center text-white/30">
                    <Minus className="mx-auto w-5 h-5 sm:w-6 sm:h-6 stroke-[2px]" />
                  </td>
                </tr>
                <tr className="hover:bg-white/[0.03] transition-colors">
                  <td className="p-4 sm:p-5 font-bold text-white/90">Storm Swath Maps</td>
                  <td className="p-4 sm:p-5 text-center bg-slate-800/30 border-x border-white/10 text-emerald-400">
                    <Check className="mx-auto w-5 h-5 sm:w-6 sm:h-6 stroke-[3px]" />
                  </td>
                  <td className="p-4 sm:p-5 text-center text-white/30">
                    <Minus className="mx-auto w-5 h-5 sm:w-6 sm:h-6 stroke-[2px]" />
                  </td>
                  <td className="p-4 sm:p-5 text-center text-white/30">
                    <Minus className="mx-auto w-5 h-5 sm:w-6 sm:h-6 stroke-[2px]" />
                  </td>
                </tr>
                <tr className="hover:bg-white/[0.03] transition-colors">
                  <td className="p-4 sm:p-5 font-bold text-white/90">CRM & Invoicing</td>
                  <td className="p-4 sm:p-5 text-center bg-slate-800/30 border-x border-white/10 text-emerald-400">
                    <Check className="mx-auto w-5 h-5 sm:w-6 sm:h-6 stroke-[3px]" />
                  </td>
                  <td className="p-4 sm:p-5 text-center text-white/80">
                    <Check className="mx-auto w-5 h-5 sm:w-6 sm:h-6 stroke-[2px]" />
                  </td>
                  <td className="p-4 sm:p-5 text-center text-white/30">
                    <Minus className="mx-auto w-5 h-5 sm:w-6 sm:h-6 stroke-[2px]" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="p-4 text-xs text-white/60 text-center bg-black/40 border-t border-white/10 leading-relaxed">
            * Comparison based on publicly available pricing as of Jan 2026. Trademarks belong to
            respective owners.
            <br />
            ** Free tier includes standard satellite imagery. Premium high-res imagery may incur
            pass-through costs.
          </div>
        </motion.div>

        {/* Reframed Product Screenshot with glass elevation */}
        <motion.section
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-28 max-w-6xl mx-auto px-4 sm:px-6 relative z-10"
        >
          <div className="relative rounded-3xl p-1 bg-gradient-to-b from-white/15 to-transparent shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent z-10 pointer-events-none rounded-3xl" />
            <div className="rounded-[22px] overflow-hidden border border-white/10 bg-[#0e071e]">
              <img
                src="/product-frames/dashboard.webp"
                alt="Rooflyzer OS dashboard — professional contractor management console"
                className="w-full h-auto object-cover transform hover:scale-[1.005] transition-transform duration-700"
                loading="lazy"
              />
            </div>
            <div className="absolute bottom-8 left-8 z-20"></div>
          </div>
        </motion.section>

        {/* Individual Tool Comparisons */}
        <div className="max-w-6xl mx-auto mb-28">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-4xl font-extrabold text-white mb-4 tracking-tight"
            >
              Detailed Feature Comparisons
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg text-white/80 max-w-2xl mx-auto leading-relaxed"
            >
              See how Rooflyzer compares to individual tools in specific categories. Understand why
              an integrated system beats fragmented tools.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <Target className="size-6" />,
                title: 'vs JobNimbus',
                competitor: 'Full CRM Comparison',
                description:
                  'Complete all-in-one platform vs fragmented roofing CRM with manual integrations.',
                link: '/compare/jobnimbus',
              },
              {
                icon: <Receipt className="size-6" />,
                title: 'vs AccuLynx',
                competitor: 'Production & Invoicing',
                description:
                  'Modern all-in-one platform vs legacy, clunky roofing management portals.',
                link: '/compare/acculynx',
              },
              {
                icon: <Map className="size-6" />,
                title: 'vs Leap',
                competitor: 'Sales & Estimating',
                description:
                  'Integrated CRM vs standalone sales enablement tablets lacking backend operations.',
                link: '/compare/leap',
              },
              {
                icon: <Calculator className="size-6" />,
                title: 'vs Roofr',
                competitor: 'Instant Estimates',
                description:
                  'Free unlimited satellite estimates vs paying $15 to $40 for every single report.',
                link: '/compare/roofr',
              },
              {
                icon: <Camera className="size-6" />,
                title: 'vs Buildertrend',
                competitor: 'Project Management',
                description:
                  'Purpose-built for specialty roofing contractors vs generic general contracting builders.',
                link: '/compare/buildertrend',
              },
              {
                icon: <Calculator className="size-6" />,
                title: 'vs Zuper',
                competitor: 'Field Service',
                description:
                  'Roofing-specific pipeline controls vs generic field service appointment schedulers.',
                link: '/compare/zuper',
              },
            ].map((comparison, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="h-full"
              >
                <Link
                  to={comparison.link}
                  className="group bg-white/[0.03] border border-white/10 hover:border-slate-600 rounded-3xl p-6 relative overflow-hidden flex flex-col justify-between h-full transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-white/[0.02] rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                  <div className="relative z-10 flex-grow">
                    <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700/80 flex items-center justify-center text-slate-200 mb-5 group-hover:scale-105 transition-transform shadow-inner">
                      {comparison.icon}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-slate-100 transition-colors">
                      {comparison.title}
                    </h3>
                    <p className="text-slate-400 text-xs font-mono font-medium uppercase tracking-wider mb-2">
                      {comparison.competitor}
                    </p>
                    <p className="text-white/80 text-sm leading-relaxed mb-4">
                      {comparison.description}
                    </p>
                  </div>

                  <div className="relative z-10 flex items-center gap-2 text-slate-300 text-sm font-semibold mt-auto pt-4 border-t border-white/5 group-hover:text-white">
                    <span>View Comparison</span>
                    <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Container */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto bg-slate-900/80 rounded-3xl p-8 sm:p-12 border border-slate-800 relative overflow-hidden z-10 shadow-2xl"
        >
          <div className="absolute inset-0 tech-grid-bg opacity-[0.05] pointer-events-none" />
          <h2 className="text-3xl font-extrabold text-white mb-4 tracking-tight">
            Ready to Replace Your Fragmented Tech Stack?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto leading-relaxed text-sm">
            Stop juggling multiple tools. Get everything you need in one integrated platform that
            grows with your business.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              size="lg"
              onClick={() => window.location.assign('/demo')}
              className="bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 rounded-xl shadow-md transition-transform transform hover:-translate-y-0.5"
            >
              See It In Action
              <ArrowRight className="ml-2 size-5" />
            </Button>
            <Button
              size="lg"
              variant="ghost"
              onClick={() => (window.location.href = '/contact')}
              className="border border-white/20 text-white hover:bg-white/10 rounded-xl transition-all"
            >
              Schedule Demo Call
            </Button>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default ComparePage;
